import React from 'react';
import classnames from 'classnames';

import BoardCredits, { type PhotoCredit } from './BoardCredits';

export type Build = {
  branch?: string;
  committedAt?: string;
  commitUrl?: string;
  shortCommit?: string;
};
type Props = {
  build?: Build | null;
  photoCredits?: Record<string, PhotoCredit>;
  variant?: string;
};

function BuildLink({ build }: { build?: Build | null }) {
  if (!build || !build.commitUrl || !build.committedAt || !build.shortCommit) {
    return null;
  }

  const branch = build.branch && build.branch !== 'main' ? build.branch : null;
  const deployedCodeLabel = branch
    ? `Currently deployed code: ${branch}; ${build.committedAt} UTC (${build.shortCommit})`
    : `Currently deployed code: ${build.committedAt} UTC (${build.shortCommit})`;

  return (
    <a
      className="site-footer-build"
      href={build.commitUrl}
      rel="noopener"
      aria-label={deployedCodeLabel}
    >
      <span className="icon-github site-footer-build-icon" aria-hidden="true" />
      {branch && (
        <>
          <span className="site-footer-build-branch">{branch}</span>
          <span className="site-footer-build-separator" aria-hidden="true">
            ·
          </span>
        </>
      )}
      <span className="site-footer-build-commit">
        {build.committedAt} UTC ({build.shortCommit})
      </span>
    </a>
  );
}

function FooterLinks() {
  return (
    <ul className="site-footer-links list-inline">
      <li>
        <a href="/support?category=volunteering">Volunteering</a>
      </li>
      <li>
        <a href="/rules">Rules</a>
      </li>
      <li>
        <a href="/safety">Safety</a>
      </li>
      <li>
        <a href="/faq">FAQ</a>
      </li>
      <li>
        <a
          href="https://wiki.trustroots.org/"
          target="_blank"
          rel="noopener noreferrer"
        >
          Wiki
        </a>
      </li>
      <li>
        <a href="/privacy">Privacy</a>
      </li>
      <li>
        <a href="/credits">Credits</a>
      </li>
      <li>
        <a href="/contact">Contact</a>
      </li>
    </ul>
  );
}

function SharedFooter({ build, photoCredits, variant }: Props) {
  const showOnMobile = variant === 'admin' || variant === 'circle';

  return (
    <footer
      id="tr-footer"
      role="contentinfo"
      className={classnames('container hidden-print', {
        'hidden-xs': !showOnMobile,
        'site-footer-circle': variant === 'circle',
      })}
    >
      <div className="site-footer-content">
        <FooterLinks />
        <div className="site-footer-meta">
          <BuildLink build={build} />
          <BoardCredits photoCredits={photoCredits || {}} />
        </div>
      </div>
    </footer>
  );
}

export default function SiteFooter({
  build,
  photoCredits,
  variant = 'standard',
}: Props) {
  if (variant === 'account') {
    return (
      <footer className="site-footer-account hidden-print" role="contentinfo">
        <BuildLink build={build} />
      </footer>
    );
  }

  if (variant === 'home') {
    return (
      <footer className="site-footer-home hidden-print" role="contentinfo">
        <a href="/credits">Credits</a>
        <BuildLink build={build} />
        <BoardCredits photoCredits={photoCredits || {}} />
      </footer>
    );
  }

  return (
    <SharedFooter build={build} photoCredits={photoCredits} variant={variant} />
  );
}
